import { Component } from '@angular/core';
import { Subject } from 'rxjs';
import { MapService } from './map.service';
import { ConfigService } from './config.service';
import { DataLayerService } from './data-layer.service';
import { UserMessageService } from './user-message.service';

/*
 * data-layer-base is the base component for all data-layer components.  It's functions include:
 *	- establishing a connection to the different services
 *	- sets up an observer so once stations are read, they are placed in the observer and drawMarkers() is automatically called
 * 	- variables such as "active" and "id" are assigned based on the configuration file
 *
 * To derive a class from data-layer-base, the component definition must be complete including the @Component section
 * as well as exporting the new Component name and extendign DataLayerBaseComponent, and a constructor which calls
 * DataLayerBaseComponent's constructor
 *
 *       --------------------------------------------------------------
 *		 @Component({
 *			selector: 'data-layer-geojson-point',
 *			template: `
 *				<div class="checkbox"><label class="overlay-layer">
 *				<input type="checkbox" (click)="menuControlSelected($event)">{{htmlElementLabel}}</label></div>
 *				`,
 *			styleUrls: ['app.component.css']
 *		})
 *		export class DataLayerGeojsonPointComponent extends DataLayerBaseComponent {
 *
 *			constructor( mapService: MapService, dataLayerService: DataLayerService,
 *				configService: ConfigService, userMessageService: UserMessageService ) {
 *				super(mapService, dataLayerService, configService, userMessageService);
 *			}
 *       --------------------------------------------------------------
 *
 */

@Component({
	selector: 'data-layer-base',
	template: `
		<div class="checkboxDiv"><label><input type="checkbox" (click)="menuControlSelected($event)">{{htmlElementLabel}}</label>
		<app-single-legend [active]=active [legendUrl]=legendUrl></app-single-legend>
		</div>
		`
})

export class DataLayerBaseComponent {
	mapService: MapService;
	dataLayerService: DataLayerService;
	configService: ConfigService;
	userMessageService: UserMessageService;

	value: any;
	valueObservable: Subject<any>;
	valueObserver;

	mapLayer: any;
	htmlElementLabel = 'Label not specified';

	active: boolean = false;
	bounds: any[];
	clusterMarkers: boolean = false;
	id: string;
	legendUrl: string;
	popupTitle: string;
	refresh: boolean = false;
	titleString: string;
	url: string;
	tooltipNSColorOnly: boolean = false;
	markerColor: string;
	iconUrl: string;

	constructor (
		mapService: MapService,
		dataLayerService: DataLayerService,
		configService: ConfigService,
		userMessageService: UserMessageService ) {

		this.mapService = mapService;
		this.dataLayerService = dataLayerService;
		this.configService = configService;
		this.userMessageService = userMessageService;

		this.valueObservable = new Subject<any>();
		this.valueObserver = this.valueObservable.asObservable();
		this.valueObserver.subscribe ( stations => {
			this.drawMarkers();
		});
	}

	menuControlSelected (event): void {

	}

	drawMarkers() {}

	setData ( someData: any ) {
		if ( someData.hasOwnProperty('label')) {
			this.htmlElementLabel = someData.label;
		}
		if ( someData.hasOwnProperty('popupTitle')) {
			this.popupTitle = someData.popupTitle;
		}
		if ( someData.hasOwnProperty('url')) {
			this.url = someData.url;
		}
		if ( someData.hasOwnProperty('refresh')) {
			this.refresh = someData.refresh;
		}
		if ( someData.hasOwnProperty('id')) {
			this.id = someData.id;
		}
		if ( someData.hasOwnProperty('titleString')) {
			this.titleString = someData.titleString;
		}
		if ( someData.hasOwnProperty('bounds')) {
			this.bounds = someData.bounds;
		}
		if ( someData.hasOwnProperty('legendUrl')) {
			this.legendUrl = someData.legendUrl;
		}
		if ( someData.hasOwnProperty('clusterMarkers')) {
			this.clusterMarkers = someData.clusterMarkers;
		}
		if ( someData.hasOwnProperty('tooltipNSColorOnly')) {
			this.tooltipNSColorOnly = someData.tooltipNSColorOnly;
		}
		if ( someData.hasOwnProperty('markerColor')) {
			this.markerColor = someData.markerColor;
		}
		if ( someData.hasOwnProperty('iconUrl')) {
			this.iconUrl = someData.iconUrl;
		}
	}

	refreshLayer() {

	}

	setValue (data) {
		this.value = data;
	}

	// verifyLayerCofiguration is passed a list of attributes required for the current layer.
	// If any of those attributes is null, a warning is added to userMessageServices' log.
	verifyLayerConfiguration ( className: string, attributes: string[] ) {

		let problemFound = false;
		const message = {};
		const items = [];

		console.log('In verifyLayerConfiguration');
		attributes.forEach(function(element) {
			// console.log(element);
			// console.log(this[element]);
			if ( this[element] === undefined ) {
				if ( !problemFound ) {
					// first time a problem was found
					problemFound = true;

					const line_i = {
						'html': 'h3',
						'message': 'Problem found in the configuration file for class ' + className + '(' + this.id + ')'
					};
					items.push ( line_i );
				}

				const line = {
					'html': 'p',
					'message': ('\u2718' + element + ', a required attribute for this class, is not specified.')
				};
				items.push ( line );

				message['error'] = items;


			}
		}, this);

		if ( problemFound ) {
			// this.userMessageService.log('\u2718' + element);
			this.userMessageService.log(message);
		}
	}

	// Takes a date string from data web services and converts it to
	// a more readable string.  Taking advantage of the fact that Sweden's
	// format is very much like ISO.
	dateToISOButLocal(dateString) {
		const dateObj = new Date(dateString);
		return dateObj.toLocaleString('sv').replace(' ', 'T');
	}

}
